import React from 'react';
import { translateLabel } from './labelUtils';
import * as MuiIcons from '@mui/icons-material';
import { getPodLinkBehavior, isPrintOnDemandSection } from 'services/Utilities/podRoutingUtils';

/**
 * Maps an icon name string to a MUI icon component
 * @param {string} iconName - Name of the MUI icon (e.g., 'Home', 'Settings')
 * @returns {React.Element|null} MUI icon component or null if not found
 */
const getMuiIcon = iconName => {
  if (!iconName || typeof iconName !== 'string') {
    return null;
  }

  const IconComponent = MuiIcons[iconName];
  return IconComponent ? <IconComponent /> : null;
};

/**
 * Builds menu items based on current route and dealer portal menu tree data
 * @param {object} params - Parameters object
 * @param {string} params.pathname - Current location pathname
 * @param {function} params.t - Translation function
 * @param {array} params.dealerPortalMenuTreeData - Menu tree data from API
 * @param {string} params.language - Current language (i18n.language)
 * @param {string} params.currentSectionName - Current section name
 * @returns {array} Array of menu items
 */
export const buildMenuItems = ({ pathname, t, dealerPortalMenuTreeData, language, currentSectionName }) => {
  let horizontalItems = [];

  if (pathname.startsWith('/dealer/certification')) {
    horizontalItems.push({
      name: t('certNav.overview')?.toUpperCase(),
      type: 'item',
      link: `/dealer/certification`,
      order: 1,
    });
    horizontalItems.push({
      name: t('certNav.submissions')?.toUpperCase(),
      type: 'item',
      link: `/dealer/certification/submissions`,
      order: 2,
      dataTour: 'navCertification',
    });
    horizontalItems.push({
      name: t('certNav.userGuide')?.toUpperCase(),
      type: 'item',
      link: `/dealer/certification/user-manual`,
      order: 3,
      dataTour: 'navGuide',
    });
  } else {
    const treeNodes = Array.isArray(dealerPortalMenuTreeData) ? dealerPortalMenuTreeData : [];
    const sortedByOrder = nodes => [...nodes].sort((left, right) => Number(left?.order ?? 0) - Number(right?.order ?? 0));
    const getChildren = node => {
      if (Array.isArray(node?.children)) {
        return node.children;
      }

      if (Array.isArray(node?.Children)) {
        return node.Children;
      }

      return [];
    };
    const getMenuId = node => node?.id || node?.Id;
    const getMenuName = node => {
      const translated = translateLabel(node?.name || node?.Name, language);
      return translated || getMenuId(node) || '';
    };
    const getMobileMenuIcon = node => node?.mobileMenuIcon || node?.MobileMenuIcon;
    const getMenuAttachedFileId = node => node?.menuAttachedFileId || node?.MenuAttachedFileId;
    const getMenuDescription = node => {
      const translated = translateLabel(node?.description || node?.Description, language);
      return translated || '';
    };
    const getMenuShortDescription = node => {
      const translated = translateLabel(node?.shortDescription || node?.ShortDescription, language);
      return translated || '';
    };
    const useLegacyPodLinkBehavior = isPrintOnDemandSection(currentSectionName);
    const buildMenuLinkData = menuId => {
      const internalLink = `/dealer/section/${currentSectionName}/menu/${menuId}`;

      if (!useLegacyPodLinkBehavior) {
        return {
          link: internalLink,
        };
      }

      const podLinkBehavior = getPodLinkBehavior(internalLink);

      return {
        link: podLinkBehavior.url,
        isExternalRoute: podLinkBehavior.isExternalRoute,
        openInNewTab: podLinkBehavior.openInNewTab,
      };
    };

    const mapChildNode = node => {
      const children = sortedByOrder(getChildren(node));
      const iconName = getMobileMenuIcon(node);
      const icon = iconName ? getMuiIcon(iconName) : undefined;

      if (children.length === 0) {
        return {
          id: getMenuId(node),
          name: getMenuName(node),
          type: 'item',
          ...buildMenuLinkData(getMenuId(node)),
          order: Number(node?.order ?? 0),
          menuAttachedFileId: getMenuAttachedFileId(node),
          description: getMenuDescription(node),
          shortDescription: getMenuShortDescription(node),
          ...(icon && { icon }),
        };
      }

      return {
        id: getMenuId(node),
        name: getMenuName(node),
        type: 'collapse',
        order: Number(node?.order ?? 0),
        menuAttachedFileId: getMenuAttachedFileId(node),
        description: getMenuDescription(node),
        shortDescription: getMenuShortDescription(node),
        children: children.map(mapChildNode),
        ...(icon && { icon }),
      };
    };

    const treeItems = sortedByOrder(treeNodes).map(node => {
      const children = sortedByOrder(getChildren(node));
      const iconName = getMobileMenuIcon(node);
      const icon = iconName ? getMuiIcon(iconName) : undefined;

      if (children.length === 0) {
        return {
          id: getMenuId(node),
          name: getMenuName(node).toUpperCase(),
          type: 'item',
          ...buildMenuLinkData(getMenuId(node)),
          order: Number(node?.order ?? 0),
          menuAttachedFileId: getMenuAttachedFileId(node),
          description: getMenuDescription(node),
          shortDescription: getMenuShortDescription(node),
          ...(icon && { icon }),
        };
      }

      return {
        id: getMenuId(node),
        name: getMenuName(node).toUpperCase(),
        type: 'collapse',
        order: Number(node?.order ?? 0),
        menuAttachedFileId: getMenuAttachedFileId(node),
        description: getMenuDescription(node),
        shortDescription: getMenuShortDescription(node),
        children: children.map(mapChildNode),
        ...(icon && { icon }),
      };
    });

    horizontalItems.push(...treeItems);
  }

  return horizontalItems;
};
